import { useState, useEffect, useCallback, useRef, type ReactNode, type CSSProperties } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Download, Plus, Pencil, Trash2,
  Users, LayoutList, BarChart3, ChevronDown, ChevronUp, Clock,
  PlusCircle, Trash, Edit, ArrowRightLeft, Printer, GripVertical,
  Share2, Copy, Unlink, Target, ExternalLink, Wallet, GitBranch, CheckCircle2,
  ChevronsUpDown, Lock, X, HelpCircle, Columns3,
} from 'lucide-react';
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors,
  useDroppable, closestCenter,
} from '@dnd-kit/core';
import type { DragStartEvent, DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext, useSortable, arrayMove, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { getProgressIndicatorStyle } from '@/lib/progress-utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { cronogramaService } from '@/services/cronogramasApi';
import {
  projetotService,
  projetotInvestimentoService,
  projetotSecaoService,
  projetotAtividadeService,
  projetotAnexoService,
  projetotResponsavelService,
} from '@/services/projetotApi';

import {
  ProjetoT, ProjetoStatus, ProjetoInvestimento,
  CronogramaSecao, CronogramaAtividade, CronogramaAtividadeAnexo,
  CronogramaAtividadeStatus, CronogramaAtividadePrioridade, CronogramaAtividadeSubtarefa, CronogramaResponsavel, CronogramaDashboard,
  CronogramaHistorico, OKR, Usuario, ProjetoTVinculos,
} from '@/types';
import { okrService, keyResultService, usuarioService } from '@/services/api';
import { AnexosSection } from '@/components/cronograma/AnexosSection';
import { PendingAnexosSection } from '@/components/cronograma/PendingAnexosSection';
import { SubtarefasSection } from '@/components/cronograma/SubtarefasSection';
import { ProjetoStatusBadge } from '@/components/projetos/ProjetoStatusBadge';
import { ProjetoStatusFlow } from '@/components/projetos/ProjetoStatusFlow';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/contexts/AuthContext';
import { useGerenciadorColunas, ColunaResizeHandle, BotaoColunas, type ColunaDef } from '@/components/table/colunas-gerenciaveis';

// ─── Helpers ────────────────────────────────────────────────────────────────

function fmtDate(val?: string | null) {
  if (!val) return '—';
  return new Date(val + 'T00:00:00').toLocaleDateString('pt-BR');
}

// Espelha percentualDaAtividade do backend (projetotRoutes.ts): atividade com checklist usa %
// de subtarefas concluídas, sem checklist usa binário por status (0% ou 100%).
function percentualAtividade(a: Pick<CronogramaAtividade, 'status' | 'subtarefas'>): number {
  if (a.subtarefas && a.subtarefas.length > 0) {
    const concluidas = a.subtarefas.filter(s => s.concluida).length;
    return Math.round((concluidas / a.subtarefas.length) * 100);
  }
  return a.status === 'finalizado' ? 100 : 0;
}

const STATUS_CONFIG: Record<CronogramaAtividadeStatus, { label: string; color: string; bg: string }> = {
  nao_iniciado: { label: 'Não iniciado', color: 'text-slate-600',   bg: 'bg-slate-200' },
  em_processo:  { label: 'Em processo',  color: 'text-amber-800',   bg: 'bg-amber-200' },
  finalizado:   { label: 'Finalizado',   color: 'text-emerald-800', bg: 'bg-emerald-200' },
  suspenso:     { label: 'Suspenso',     color: 'text-rose-800',    bg: 'bg-rose-200' },
};

const PRIORIDADE_CONFIG: Record<CronogramaAtividadePrioridade, { label: string; color: string; bg: string }> = {
  baixa:   { label: 'Baixa',   color: 'text-slate-600',   bg: 'bg-slate-200' },
  media:   { label: 'Média',   color: 'text-sky-800',     bg: 'bg-sky-200' },
  alta:    { label: 'Alta',    color: 'text-amber-800',   bg: 'bg-amber-200' },
  urgente: { label: 'Urgente', color: 'text-rose-800',    bg: 'bg-rose-200' },
};

function PrioridadeBadge({ prioridade }: { prioridade: CronogramaAtividadePrioridade }) {
  const cfg = PRIORIDADE_CONFIG[prioridade] || PRIORIDADE_CONFIG.media;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cfg.bg} ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

function StatusBadge({ status }: { status: CronogramaAtividadeStatus }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.nao_iniciado;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cfg.bg} ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

// Calcula o texto/estilo da coluna "Prazo" a partir de status + data_termino — não existe
// como campo persistido, é derivado na hora (atraso = data_termino no passado e não finalizado).
function prazoInfo(atv: Pick<CronogramaAtividade, 'status' | 'data_termino'>): { label: string; className: string } {
  if (atv.status === 'finalizado') {
    return { label: atv.data_termino ? `concluída ${fmtDate(atv.data_termino)}` : 'concluída', className: 'text-muted-foreground' };
  }
  if (!atv.data_termino) return { label: 'sem prazo', className: 'text-muted-foreground' };
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const termino = new Date(atv.data_termino + 'T00:00:00');
  const diffDias = Math.round((termino.getTime() - hoje.getTime()) / 86400000);
  if (diffDias < 0) {
    const dias = Math.abs(diffDias);
    return { label: `atrasada ${dias} dia${dias === 1 ? '' : 's'}`, className: 'text-rose-600 font-semibold' };
  }
  if (diffDias === 0) return { label: 'vence hoje', className: 'text-amber-600 font-medium' };
  if (diffDias <= 7) return { label: `vence em ${diffDias} dia${diffDias === 1 ? '' : 's'}`, className: 'text-amber-600' };
  return { label: fmtDate(atv.data_termino), className: 'text-muted-foreground' };
}

// Colunas da linha de atividade que dão pra mostrar/ocultar/redimensionar (mesmo gerenciador
// reutilizável da lista de Projetos, `useGerenciadorColunas` — preferência por navegador,
// localStorage). "Cód." (carrega o drag-handle + dropdown de subatividades) e "Ações" ficam de
// fora do gerenciador, sempre visíveis nas pontas — mesmo padrão de `COLUNAS_LISTA_PROJETOS`.
export type ColunaAtividadeId =
  'titulo' | 'descricao' | 'responsaveis' | 'inicio' | 'termino' | 'status' | 'prioridade' | 'prazo' | 'vinculo';

const COLUNAS_ATIVIDADES: ColunaDef<ColunaAtividadeId>[] = [
  { id: 'titulo', label: 'Atividade', defaultWidth: 320, minWidth: 160, defaultVisible: true, travada: true },
  { id: 'descricao', label: 'Descrição', defaultWidth: 320, minWidth: 160, defaultVisible: true },
  { id: 'responsaveis', label: 'Responsáveis', defaultWidth: 100, minWidth: 72, defaultVisible: true },
  { id: 'inicio', label: 'Início', defaultWidth: 76, minWidth: 64, defaultVisible: true },
  { id: 'termino', label: 'Término', defaultWidth: 76, minWidth: 64, defaultVisible: true },
  { id: 'status', label: 'Status', defaultWidth: 112, minWidth: 92, defaultVisible: true },
  { id: 'prioridade', label: 'Prioridade', defaultWidth: 84, minWidth: 72, defaultVisible: true },
  { id: 'prazo', label: 'Prazo', defaultWidth: 100, minWidth: 80, defaultVisible: true },
  { id: 'vinculo', label: 'OKR', defaultWidth: 104, minWidth: 88, defaultVisible: true },
];

const ATIVIDADE_COD_COL = '112px';
const ATIVIDADE_ACOES_COL = '64px';

function gridColsAtividade(colunas: { width: number }[]): string {
  return `${ATIVIDADE_COD_COL} ${colunas.map(c => `${c.width}px`).join(' ')} ${ATIVIDADE_ACOES_COL}`;
}

function AtividadesHeader({
  colunas, onResize,
}: {
  colunas: { id: ColunaAtividadeId; width: number; label: string }[];
  onResize: (id: ColunaAtividadeId, deltaX: number) => void;
}) {
  return (
    <div
      className="grid items-center gap-x-2 px-5 py-1.5 border-t bg-muted/30 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
      style={{ gridTemplateColumns: gridColsAtividade(colunas) }}
    >
      <span>Cód.</span>
      {colunas.map(c => (
        <span key={c.id} className="relative overflow-hidden truncate pr-2">
          {c.label}
          <ColunaResizeHandle onResize={deltaX => onResize(c.id, deltaX)} />
        </span>
      ))}
      <span className="text-right">Ações</span>
    </div>
  );
}

// ─── Tipo do formulário de atividade ────────────────────────────────────────

type AtividadeFormState = Partial<CronogramaAtividade> & { responsaveis_ids: string[] };

// Rolagem horizontal "arrastar pra deslizar" (clicar e segurar com o mouse) — a tabela de
// atividades fica mais larga que a tela em telas estreitas/com colunas extras ativadas; roda
// normal (trackpad/scrollbar) já funciona via overflow-x-auto, isto só soma o arrastar com
// mouse. Ignora o gesto se começar em cima de um controle (botão, input, select etc.) pra não
// atrapalhar clique/drag de linha (drag-handle) ou o redimensionar de coluna.
function DragScrollX({ children, className, style }: { children: ReactNode; className?: string; style?: CSSProperties }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let arrastando = false;
    let inicioX = 0;
    let scrollInicial = 0;
    const onDown = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest('button, a, input, select, [role="button"], [data-radix-select-trigger]')) return;
      arrastando = true;
      inicioX = e.clientX;
      scrollInicial = el.scrollLeft;
      el.classList.add('cursor-grabbing');
    };
    const onMove = (e: MouseEvent) => {
      if (!arrastando) return;
      e.preventDefault();
      el.scrollLeft = scrollInicial - (e.clientX - inicioX);
    };
    const parar = () => { arrastando = false; el.classList.remove('cursor-grabbing'); };
    el.addEventListener('mousedown', onDown);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', parar);
    return () => {
      el.removeEventListener('mousedown', onDown);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', parar);
    };
  }, []);
  return (
    <div ref={ref} className={cn('overflow-x-auto cursor-grab', className)} style={style}>
      {children}
    </div>
  );
}

// ─── Componentes auxiliares de Drag & Drop ───────────────────────────────────

function SecaoDropZone({ id, children }: { id: string; children: ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div ref={setNodeRef} className={isOver ? 'ring-2 ring-primary ring-inset rounded-b' : ''}>
      {children}
    </div>
  );
}

function rectsOverlap(a: { top: number; left: number; right: number; bottom: number }, b: typeof a): boolean {
  return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}

function sortAtividades(atividades: CronogramaAtividade[]): CronogramaAtividade[] {
  return [...atividades].sort((a, b) =>
    a.codigo.localeCompare(b.codigo, undefined, { numeric: true, sensitivity: 'base' }) ||
    new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime()
  );
}

// Iniciais pro avatar circular (ex: "Ana Souza" -> "AS").
function iniciais(nome: string): string {
  // Só letras contam como "palavra" — nomes de teste com parênteses (ex: "Admin (dev)") não
  // devem virar iniciais com "(" dentro.
  const palavras = nome.trim().match(/\p{L}+/gu) || [];
  if (palavras.length === 0) return '';
  const primeira = palavras[0][0];
  const ultima = palavras.length > 1 ? palavras[palavras.length - 1][0] : '';
  return (primeira + ultima).toUpperCase();
}

// Lista de responsáveis da atividade (mesma resolução responsaveis[] com fallback pro
// responsavel único legado) — reaproveitada pelos avatares e pelo tooltip da célula.
function listaResponsaveis(atv: CronogramaAtividade) {
  return (atv.responsaveis && atv.responsaveis.length > 0)
    ? atv.responsaveis
    : (atv.responsavel ? [atv.responsavel] : []);
}

function nomesResponsaveis(atv: CronogramaAtividade): string | undefined {
  const lista = listaResponsaveis(atv);
  return lista.length > 0 ? lista.map(r => r.nome).join(', ') : undefined;
}

function AvataresResponsaveis({ atv }: { atv: CronogramaAtividade }) {
  const lista = listaResponsaveis(atv);
  if (lista.length === 0) return null;
  const visiveis = lista.slice(0, 3);
  return (
    <div className="flex items-center shrink-0">
      {visiveis.map((r, i) => (
        <div
          key={r.id}
          title={r.nome}
          className="h-6 w-6 rounded-full bg-muted border-2 border-card flex items-center justify-center text-[10px] font-semibold"
          style={i > 0 ? { marginLeft: '-8px' } : undefined}
        >
          {iniciais(r.nome)}
        </div>
      ))}
      {lista.length > visiveis.length && (
        <div className="h-6 w-6 rounded-full bg-muted border-2 border-card flex items-center justify-center text-[9px] font-semibold text-muted-foreground" style={{ marginLeft: '-8px' }}>
          +{lista.length - visiveis.length}
        </div>
      )}
    </div>
  );
}

function AtividadeRow({
  atv, podeEditar, podeConverter, onStatusChange, onOkrClick, onConvert, onEdit, onDelete,
  cronogramaId, subatividadesExpandida, onToggleSubatividades, onSubatividadesChange, colunas,
}: {
  atv: CronogramaAtividade;
  podeEditar: boolean;
  // Converter em OKR/Key Result cria item no módulo OKRs, que é só de admin.
  podeConverter: boolean;
  onStatusChange: (id: string, status: CronogramaAtividadeStatus) => void;
  // Abre em /okrs o OKR ligado à atividade (o dela mesma, ou o OKR do Key Result dela).
  onOkrClick: (atv: CronogramaAtividade) => void;
  onConvert: (atv: CronogramaAtividade) => void;
  onEdit: (atv: CronogramaAtividade) => void;
  onDelete: (atv: CronogramaAtividade) => void;
  cronogramaId: string;
  subatividadesExpandida: boolean;
  onToggleSubatividades: () => void;
  onSubatividadesChange: (subtarefas: CronogramaAtividadeSubtarefa[]) => void;
  colunas: { id: ColunaAtividadeId; width: number; label: string }[];
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: atv.id });
  const style: CSSProperties = { transform: CSS.Transform.toString(transform), transition };
  const totalSub = atv.subtarefas?.length || 0;
  const concluidasSub = atv.subtarefas?.filter(s => s.concluida).length || 0;
  const bloqueada = atv.status === 'nao_iniciado' && !!atv.dependeDe && atv.dependeDe.status !== 'finalizado';
  const prazo = prazoInfo(atv);

  // Conteúdo de cada coluna opcional (mostrar/ocultar/reordenar via "Colunas") — "Cód." e
  // "Ações" ficam fixos nas pontas, fora deste switch (ver AtividadesHeader).
  function celula(colId: ColunaAtividadeId): ReactNode {
    switch (colId) {
      case 'titulo':
        return (
          <div className="flex items-center gap-1.5 min-w-0">
            <span className={cn('font-medium truncate', atv.status === 'finalizado' && 'line-through text-muted-foreground')}>
              {atv.titulo}
            </span>
            {bloqueada && (
              <span title={`Bloqueada até "${atv.dependeDe!.titulo}" ser finalizada`}>
                <Lock className="h-3 w-3 shrink-0 text-muted-foreground" />
              </span>
            )}
          </div>
        );
      case 'descricao':
        return <span className="text-xs text-muted-foreground truncate">{atv.detalhamento || '—'}</span>;
      case 'responsaveis':
        return <AvataresResponsaveis atv={atv} />;
      case 'inicio':
        return <span className="text-xs text-muted-foreground">{fmtDate(atv.data_inicio)}</span>;
      case 'termino':
        return <span className="text-xs text-muted-foreground">{fmtDate(atv.data_termino)}</span>;
      case 'status':
        return podeEditar ? (
          <Select value={atv.status} onValueChange={(val) => onStatusChange(atv.id, val as CronogramaAtividadeStatus)}>
            <SelectTrigger
              onClick={e => e.stopPropagation()}
              className="h-auto w-fit shrink-0 p-0 border-0 bg-transparent [&>svg]:hidden"
            >
              <StatusBadge status={atv.status} />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(STATUS_CONFIG).map(([val, cfg]) => (
                <SelectItem key={val} value={val} className="text-xs">{cfg.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <StatusBadge status={atv.status} />
        );
      case 'prioridade':
        return <PrioridadeBadge prioridade={atv.prioridade} />;
      case 'prazo':
        return <span className={cn('text-xs', prazo.className)}>{prazo.label}</span>;
      case 'vinculo':
        return (atv.okr_gerado_id || atv.key_result_gerado_id) ? (
          <button
            type="button"
            onClick={e => { e.stopPropagation(); onOkrClick(atv); }}
            title={atv.okr_gerado_id
              ? 'Esta atividade virou um OKR — clique para abrir'
              : 'Esta atividade virou um Key Result — clique para abrir o OKR dele'}
            className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-600 hover:bg-emerald-500/20 dark:text-emerald-400"
          >
            <CheckCircle2 className="h-3 w-3" />
            {atv.okr_gerado_id ? 'OKR' : 'KR'}
            <ExternalLink className="h-2.5 w-2.5" />
          </button>
        ) : podeConverter ? (
          <Button
            variant="outline"
            size="sm"
            className="h-6 gap-1 px-2 text-[11px] text-primary hover:text-primary"
            title="Converter em Key Result do OKR da seção (o OKR é criado se ainda não existir)"
            onClick={e => { e.stopPropagation(); onConvert(atv); }}
          >
            <Target className="h-3 w-3" />
            Gerar KR
          </Button>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        );
    }
  }

  return (
    <div ref={setNodeRef} style={style} className={isDragging ? 'opacity-40' : undefined}>
      <div
        className="group grid items-center gap-x-2 px-5 py-2.5 border-t cursor-pointer hover:bg-muted/40 transition-colors"
        style={{ gridTemplateColumns: gridColsAtividade(colunas) }}
        onClick={() => onEdit(atv)}
      >
        {/* Cód. — arrastar (admin, hover) + dropdown de subatividades + código, tudo à esquerda */}
        <div className="flex items-center gap-1 min-w-0">
          {podeEditar && (
            <button
              {...attributes}
              {...listeners}
              onClick={e => e.stopPropagation()}
              className="cursor-grab active:cursor-grabbing p-0.5 rounded hover:bg-muted touch-none shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100"
              title="Arrastar para reordenar ou mover para outra seção"
              type="button"
            >
              <GripVertical className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            type="button"
            onClick={e => { e.stopPropagation(); onToggleSubatividades(); }}
            className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground hover:bg-muted"
            title={totalSub > 0 ? `${concluidasSub}/${totalSub} subatividades` : 'Adicionar subatividades'}
          >
            {subatividadesExpandida ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>
          <span className="font-mono text-[11px] text-muted-foreground truncate">{atv.codigo}</span>
        </div>

        {colunas.map(c => (
          <div key={c.id} className="min-w-0" title={c.id === 'responsaveis' ? nomesResponsaveis(atv) : undefined}>
            {celula(c.id)}
          </div>
        ))}

        <div className="flex items-center gap-0.5 justify-end shrink-0" onClick={(e) => e.stopPropagation()}>
          {podeEditar && (
            <>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(atv)}>
                <Pencil className="h-3 w-3" />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={() => onDelete(atv)}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </>
          )}
        </div>
      </div>

      {subatividadesExpandida && (
        <div className="border-t bg-muted/20 px-5 py-3" style={{ paddingLeft: `calc(1.25rem + ${ATIVIDADE_COD_COL} + 0.75rem)` }}>
          <SubtarefasSection
            cronogramaId={cronogramaId}
            atividadeId={atv.id}
            subtarefas={atv.subtarefas || []}
            onSubtarefasChange={onSubatividadesChange}
          />
        </div>
      )}
    </div>
  );
}

function ResponsavelTable({
  list, podeEditar, onEdit, onDelete,
}: {
  list: CronogramaResponsavel[];
  podeEditar: boolean;
  onEdit: (r: CronogramaResponsavel) => void;
  onDelete: (r: CronogramaResponsavel) => void;
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Nome</TableHead>
          <TableHead>Cargo</TableHead>
          <TableHead>E-mail</TableHead>
          <TableHead className="w-20 text-right">Ações</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {list.map(r => (
          <TableRow key={r.id}>
            <TableCell className="font-medium">{r.nome}</TableCell>
            <TableCell className="text-muted-foreground">{r.cargo || '—'}</TableCell>
            <TableCell className="text-muted-foreground">{r.email || '—'}</TableCell>
            <TableCell className="text-right">
              {podeEditar && (
                <div className="flex justify-end gap-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(r)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={() => onDelete(r)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// ─── Formulários ────────────────────────────────────────────────────────────

const EMPTY_SECAO = { numero: 1, titulo: '', ordem: 0, data_inicio: '', data_termino: '' };
const EMPTY_ATIV: AtividadeFormState = {
  codigo: '', titulo: '', detalhamento: '',
  responsaveis_ids: [],
  data_inicio: '', data_termino: '', status: 'nao_iniciado', prioridade: 'media', acompanhamento: '',
  anexo_obrigatorio: false,
  depende_de_id: null,
};
const EMPTY_RESP = { nome: '', cargo: '', email: '' };
const EMPTY_INVEST = { item: '', valor: 0, quantidade: 1 };

const brl = (valor: number) =>
  valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// ─── Componente principal ────────────────────────────────────────────────────

export default function GerenciarProjetoT() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const ehAdmin = user?.tipo === 'admin' || user?.tipo === 'superadmin';

  const [projeto, setProjeto] = useState<ProjetoT | null>(null);
  // Usuário comum edita os projetos que lidera ou criou (decisão de 2026-09-23); aprovar,
  // reprovar e validar continuam só com admin (backend: projetotRoutes.ts).
  const podeEditar = ehAdmin || (!!projeto && !!user && (projeto.lider_id === user.id || projeto.criado_por === user.id));
  const [dashboard, setDashboard] = useState<CronogramaDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Modais seção
  const [secaoModal, setSecaoModal] = useState(false);
  const [secaoForm, setSecaoForm] = useState(EMPTY_SECAO);
  const [secaoEdit, setSecaoEdit] = useState<CronogramaSecao | null>(null);
  const [secaoDelete, setSecaoDelete] = useState<CronogramaSecao | null>(null);

  // DnD
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [pendingMove, setPendingMove] = useState<{ atividade: CronogramaAtividade; targetSecao: CronogramaSecao } | null>(null);

  // Modais atividade
  const [atividadeModal, setAtividadeModal] = useState(false);
  const [atividadeForm, setAtividadeForm] = useState<AtividadeFormState>(EMPTY_ATIV);
  const [atividadeSecaoId, setAtividadeSecaoId] = useState('');
  const [atividadeEdit, setAtividadeEdit] = useState<CronogramaAtividade | null>(null);
  const [atividadeDelete, setAtividadeDelete] = useState<CronogramaAtividade | null>(null);
  const [anexos, setAnexos] = useState<CronogramaAtividadeAnexo[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  // Dropdown de subatividades por linha da tabela (independente do modal de editar atividade —
  // ver ATUALIZACOES.md "checklist saiu do modal, vive só nesse dropdown da linha").
  const [subatividadesExpandidas, setSubatividadesExpandidas] = useState<Set<string>>(new Set());
  const toggleSubatividades = (atividadeId: string) => setSubatividadesExpandidas(prev => {
    const next = new Set(prev);
    next.has(atividadeId) ? next.delete(atividadeId) : next.add(atividadeId);
    return next;
  });

  // Conversão de atividade em OKR / Key Result
  const [convertAtiv, setConvertAtiv] = useState<CronogramaAtividade | null>(null);
  const [convertOkrSecao, setConvertOkrSecao] = useState<OKR | null>(null);
  const [convertLoading, setConvertLoading] = useState(false);
  const [converting, setConverting] = useState(false);

  // Modais responsável
  const [respModal, setRespModal] = useState(false);
  const [respForm, setRespForm] = useState(EMPTY_RESP);
  const [respEdit, setRespEdit] = useState<CronogramaResponsavel | null>(null);
  const [respDelete, setRespDelete] = useState<CronogramaResponsavel | null>(null);
  const [criandoRespUsuarioId, setCriandoRespUsuarioId] = useState<string | null>(null);
  const [criadosNomeById, setCriadosNomeById] = useState<Record<string, string>>({});
  const [respSelectOpen, setRespSelectOpen] = useState(false);
  const [respSelectSearch, setRespSelectSearch] = useState('');

  // Investimentos — no módulo antigo eram capturados mas nunca exibidos; aqui ficam visíveis
  // com o total somado.
  const [investimentos, setInvestimentos] = useState<ProjetoInvestimento[]>([]);
  const [investModal, setInvestModal] = useState(false);
  const [investForm, setInvestForm] = useState(EMPTY_INVEST);
  const [investEdit, setInvestEdit] = useState<ProjetoInvestimento | null>(null);
  const [investDelete, setInvestDelete] = useState<ProjetoInvestimento | null>(null);

  // Workflow de aprovação
  const [statusFlowOpen, setStatusFlowOpen] = useState(false);

  const [saving, setSaving] = useState(false);

  // Compartilhamento público
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareToken, setShareToken] = useState<string | null>(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [expandidas, setExpandidas] = useState<Set<string>>(new Set());
  const [filtroSecao, setFiltroSecao] = useState('');
  const [filtroStatus, setFiltroStatus] = useState<CronogramaAtividadeStatus | ''>('');
  const colGerenciadorAtividades = useGerenciadorColunas(COLUNAS_ATIVIDADES, 'projetot-colunas-atividades-v3');

  const [historico, setHistorico] = useState<CronogramaHistorico[]>([]);
  const [vinculos, setVinculos] = useState<ProjetoTVinculos>({ organogramas: [], fluxogramas: [] });

  const expandidasInicializadas = useRef(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    // Dashboard por status/responsável e histórico vêm das rotas de Cronograma: um ProjetoT
    // é fisicamente um registro de projeto, então elas já respondem sobre ele.
    const [cRes, dRes, hRes, vRes] = await Promise.all([
      projetotService.getById(id),
      cronogramaService.getDashboard(id),
      cronogramaService.getHistorico(id),
      projetotService.getVinculos(id),
    ]);
    if (vRes.success) setVinculos(vRes.data);
    if (cRes.success) {
      setProjeto(cRes.data);
      setInvestimentos(cRes.data.investimentos || []);
      setShareToken(cRes.data.share_token ?? null);
      if (!expandidasInicializadas.current) {
        expandidasInicializadas.current = true;
        const secoes = cRes.data.secoes || [];
        const secoesComAtividades = secoes.filter((s: any) => (s.atividades || []).length > 0);
        // Com mais de uma seção com atividades, inicia recolhido para não sobrecarregar a tela
        setExpandidas(secoesComAtividades.length > 1 ? new Set() : new Set(secoes.map((s: any) => s.id)));
      }
    } else toast.error(cRes.error || 'Erro ao carregar projeto');
    if (dRes.success) setDashboard(dRes.data);
    if (hRes.success) setHistorico(hRes.data);
    setLoading(false);
  }, [id]);

  useEffect(() => { expandidasInicializadas.current = false; }, [id]);
  useEffect(() => { load(); }, [load]);

  // Usuários da empresa — só pra distinguir, na aba Responsáveis, quem tem conta no sistema
  // (interno) de quem é só um contato cadastrado à mão (externo). Comparação por e-mail, sem
  // mudança de schema: `CronogramaResponsavel` não tem (e não precisa de) FK pra `usuarios`.
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  useEffect(() => {
    usuarioService.getAll().then(res => { if (res.success && res.data) setUsuarios(res.data); });
  }, []);

  // ── Cálculos de progresso
  // Mesma regra do backend (progressoDasAtividades em projetotRoutes.ts): atividade com
  // checklist usa % de subtarefas concluídas, sem checklist usa binário por status. Progresso do
  // projeto = média por atividade, não mais "quantas estão finalizadas".
  const todasAtividades = (projeto?.secoes || []).flatMap(s => s.atividades || []);
  const totalAtiv = todasAtividades.length;
  const progresso = totalAtiv > 0
    ? Math.round(todasAtividades.reduce((soma, a) => soma + percentualAtividade(a), 0) / totalAtiv)
    : 0;

  // ── Workflow de aprovação
  const handleStatusProjetoChange = async (novoStatus: ProjetoStatus) => {
    if (!id) return;
    const res = await projetotService.updateStatus(id, novoStatus);
    if (res.success) {
      toast.success('Status atualizado');
      load();
    } else {
      toast.error(res.error || 'Erro ao atualizar status');
    }
  };

  // ── Investimentos
  const openInvestCreate = () => { setInvestEdit(null); setInvestForm(EMPTY_INVEST); setInvestModal(true); };

  const openInvestEdit = (inv: ProjetoInvestimento) => {
    setInvestEdit(inv);
    setInvestForm({ item: inv.item, valor: Number(inv.valor), quantidade: inv.quantidade });
    setInvestModal(true);
  };

  const handleInvestSave = async () => {
    if (!id || !investForm.item) { toast.error('Item é obrigatório'); return; }
    setSaving(true);
    const res = investEdit?.id
      ? await projetotInvestimentoService.update(id, investEdit.id, investForm)
      : await projetotInvestimentoService.create(id, investForm);
    if (res.success) {
      toast.success(investEdit ? 'Investimento atualizado' : 'Investimento adicionado');
      setInvestModal(false);
      load();
    } else toast.error(res.error || 'Erro ao salvar investimento');
    setSaving(false);
  };

  const handleInvestDelete = async () => {
    if (!id || !investDelete?.id) return;
    const res = await projetotInvestimentoService.delete(id, investDelete.id);
    if (res.success) { toast.success('Investimento removido'); setInvestDelete(null); load(); }
    else toast.error(res.error || 'Erro ao excluir investimento');
  };

  const totalInvestimentos = investimentos.reduce(
    (soma, inv) => soma + Number(inv.valor) * inv.quantidade,
    0,
  );

  // ── Export
  const handleExport = async () => {
    if (!id) return;
    setExporting(true);
    try {
      await cronogramaService.exportExcel(id);
      toast.success('Excel exportado com sucesso');
    } catch {
      toast.error('Erro ao exportar Excel');
    }
    setExporting(false);
  };

  // ── Compartilhamento público
  const handleCompartilhar = async () => {
    if (!id) return;
    setShareLoading(true);
    const res = await cronogramaService.compartilhar(id);
    if (res.success) {
      setShareToken(res.data.share_token);
      toast.success('Link gerado com sucesso');
    } else {
      toast.error(res.error || 'Erro ao gerar link');
    }
    setShareLoading(false);
  };

  const handleRevogarLink = async () => {
    if (!id) return;
    setShareLoading(true);
    const res = await cronogramaService.revogarCompartilhamento(id);
    if (res.success) {
      setShareToken(null);
      toast.success('Link revogado');
    } else {
      toast.error(res.error || 'Erro ao revogar link');
    }
    setShareLoading(false);
  };

  // ── Status inline
  const handleStatusChange = async (atividadeId: string, status: CronogramaAtividadeStatus) => {
    if (!id) return;
    const res = await projetotAtividadeService.updateStatus(id, atividadeId, status);
    if (res.success) {
      load();
    } else {
      toast.error(res.error || 'Erro ao atualizar status');
    }
  };

  // ── Conversão de atividade em OKR / Key Result: a seção vira o OKR (criado se ainda não existir)
  // e a atividade vira um Key Result dele. Sem opções — o modal só confirma.
  // O OKR atual da seção é buscado ao abrir o modal: mostra o título real (pode ter sido renomeado) e
  // avisa quando ele está cancelado/feito — nesse caso a conversão cria um OKR novo para a seção.
  const openConvert = async (atv: CronogramaAtividade) => {
    setConvertAtiv(atv);
    setConvertOkrSecao(null);
    const okrId = projeto?.secoes?.find(s => s.id === atv.secao_id)?.okr_gerado_id;
    if (!okrId) return;
    setConvertLoading(true);
    const res = await okrService.getById(okrId);
    if (res.success && res.data) setConvertOkrSecao(res.data);
    setConvertLoading(false);
  };

  // Atividade convertida em KR abre o OKR do próprio KR (que pode não ser o da seção, em conversões antigas).
  const abrirOkrDaAtividade = async (atv: CronogramaAtividade) => {
    if (atv.okr_gerado_id) { navigate(`/okrs?okr=${atv.okr_gerado_id}`); return; }
    const res = atv.key_result_gerado_id ? await keyResultService.getById(atv.key_result_gerado_id) : null;
    navigate(res?.success && res.data ? `/okrs?okr=${res.data.okr_id}` : '/okrs');
  };
  // Atividades da seção já convertidas (KR, ou OKR pela regra antiga) — contagem do selo "OKR x/y".
  const secaoConvertidas = (secao: CronogramaSecao) =>
    (secao.atividades || []).filter(a => a.key_result_gerado_id || a.okr_gerado_id).length;
  const convertSecao = convertAtiv ? projeto?.secoes?.find(s => s.id === convertAtiv.secao_id) : undefined;

  const handleConverter = async () => {
    if (!id || !convertAtiv) return;
    setConverting(true);
    const res = await projetotAtividadeService.converter(id, convertAtiv.id);
    if (res.success) {
      toast.success(res.data.okrCriado
        ? `OKR "${res.data.okr.titulo}" criado com o Key Result "${res.data.keyResult.descricao}"`
        : `Key Result "${res.data.keyResult.descricao}" adicionado ao OKR "${res.data.okr.titulo}"`);
      setConvertAtiv(null);
      await load();
    } else {
      toast.error(res.error || 'Erro ao converter atividade');
    }
    setConverting(false);
  };

  // ────── SEÇÕES ──────────────────────────────────────────────────────────────

  const openSecaoCreate = () => {
    const nextNum = (projeto?.secoes?.length ?? 0) + 1;
    setSecaoEdit(null);
    setSecaoForm({ numero: nextNum, titulo: '', ordem: nextNum, data_inicio: '', data_termino: '' });
    setSecaoModal(true);
  };

  const openSecaoEdit = (s: CronogramaSecao) => {
    setSecaoEdit(s);
    setSecaoForm({ numero: s.numero, titulo: s.titulo, ordem: s.ordem, data_inicio: s.data_inicio || '', data_termino: s.data_termino || '' });
    setSecaoModal(true);
  };

  const handleSecaoSave = async () => {
    if (!id || !secaoForm.titulo) { toast.error('Título é obrigatório'); return; }
    setSaving(true);
    const res = secaoEdit
      ? await projetotSecaoService.update(id, secaoEdit.id, secaoForm)
      : await projetotSecaoService.create(id, secaoForm);
    if (res.success) {
      toast.success(secaoEdit ? 'Seção atualizada' : 'Seção criada');
      setSecaoModal(false);
      if (!secaoEdit && res.data?.id) {
        // Nova seção deve nascer expandida, já que load() só inicializa o Set uma vez por projeto
        setExpandidas(prev => new Set(prev).add(res.data.id));
      }
      load();
    }
    else toast.error(res.error || 'Erro ao salvar seção');
    setSaving(false);
  };

  const handleSecaoDelete = async () => {
    if (!id || !secaoDelete) return;
    const res = await projetotSecaoService.delete(id, secaoDelete.id);
    if (res.success) { toast.success('Seção excluída'); setSecaoDelete(null); load(); }
    else toast.error(res.error || 'Erro ao excluir seção');
  };

  // ────── ATIVIDADES ──────────────────────────────────────────────────────────

  const proximoCodigo = (secaoId: string): string => {
    const secao = (projeto?.secoes || []).find(s => s.id === secaoId);
    if (!secao) return '';
    const prefixo = String(secao.numero);
    const atividades = secao.atividades || [];
    const sufixos = atividades
      .map(a => {
        const match = a.codigo.match(new RegExp(`^${prefixo}\\.(\\d+)$`));
        return match ? parseInt(match[1], 10) : null;
      })
      .filter((n): n is number => n !== null);
    const proximo = sufixos.length > 0 ? Math.max(...sufixos) + 1 : 1;
    return `${prefixo}.${proximo}`;
  };

  const openAtivCreate = (secaoId: string) => {
    setAtividadeEdit(null);
    setAtividadeSecaoId(secaoId);
    setAtividadeForm({ ...EMPTY_ATIV, codigo: proximoCodigo(secaoId) });
    setAnexos([]);
    setPendingFiles([]);
    setAtividadeModal(true);
  };

  const openAtivEdit = async (a: CronogramaAtividade) => {
    setAtividadeEdit(a);
    setAtividadeSecaoId(a.secao_id);
    setAtividadeForm({
      codigo: a.codigo, titulo: a.titulo, detalhamento: a.detalhamento || '',
      responsaveis_ids: (a.responsaveis && a.responsaveis.length > 0)
        ? a.responsaveis.map(r => r.id)
        : (a.responsavel_id ? [a.responsavel_id] : []),
      data_inicio: a.data_inicio,
      data_termino: a.data_termino, status: a.status, prioridade: a.prioridade || 'media', acompanhamento: a.acompanhamento || '',
      anexo_obrigatorio: a.anexo_obrigatorio ?? false,
      depende_de_id: a.depende_de_id ?? null,
    });
    setAnexos([]);
    setPendingFiles([]);
    if (id) {
      const res = await projetotAnexoService.list(id, a.id);
      if (res.success) setAnexos(res.data);
    }
    setAtividadeModal(true);
  };

  const nextCodigoFromCurrent = (codigo: string): string => {
    const match = codigo.match(/^(.+\.)(\d+)$/);
    if (!match) return codigo;
    return `${match[1]}${parseInt(match[2], 10) + 1}`;
  };

  // Limite de uploads simultâneos: paralelo dentro do lote (bem mais rápido que sequencial
  // pra 2-4 arquivos, o caso comum), mas sem disparar dezenas de requisições de uma vez só
  // se o usuário selecionar muitos arquivos — cada upload faz 3 idas ao banco compartilhado.
  const UPLOAD_CONCURRENCY = 4;

  const uploadPendingFiles = async (
    cronogramaId: string,
    atividadeId: string,
  ): Promise<{ enviados: CronogramaAtividadeAnexo[]; falharam: File[] }> => {
    if (pendingFiles.length === 0) return { enviados: [], falharam: [] };
    const enviados: CronogramaAtividadeAnexo[] = [];
    const falharam: File[] = [];
    for (let i = 0; i < pendingFiles.length; i += UPLOAD_CONCURRENCY) {
      const lote = pendingFiles.slice(i, i + UPLOAD_CONCURRENCY);
      const resultados = await Promise.all(
        lote.map(file => projetotAnexoService.upload(cronogramaId, atividadeId, file).then(res => ({ file, res })))
      );
      for (const { file, res } of resultados) {
        if (res.success) enviados.push(res.data);
        else {
          falharam.push(file);
          toast.error(`Erro ao enviar "${file.name}": ${res.error || 'falha no upload'}`);
        }
      }
    }
    return { enviados, falharam };
  };

  const handleAtivSave = async (keepOpen = false) => {
    if (!id) return;
    if (!atividadeForm.codigo || !atividadeForm.titulo) {
      toast.error('Código e título são obrigatórios');
      return;
    }
    setSaving(true);
    const { responsaveis_ids, ...formRest } = atividadeForm;
    const payload = { ...formRest, secao_id: atividadeSecaoId, responsaveis_ids: responsaveis_ids ?? [] };
    const res = atividadeEdit
      ? await projetotAtividadeService.update(id, atividadeEdit.id, payload)
      : await projetotAtividadeService.create(id, payload);
    if (res.success) {
      toast.success(atividadeEdit ? 'Atividade atualizada' : 'Atividade criada');
      if (!atividadeEdit && res.data?.id) {
        const { enviados: anexosEnviados, falharam } = await uploadPendingFiles(id, res.data.id);
        setPendingFiles([]);
        if (falharam.length > 0) {
          // Upload de anexo falhou: não fecha nem reseta, senão perde o rastro do que não foi
          // enviado. Transição pro modo edição da atividade recém-criada pra reenviar por ali.
          await load();
          setAtividadeEdit(res.data);
          setAnexos(anexosEnviados);
        } else if (keepOpen) {
          // "Salvar e adicionar outro": reseta o formulário pra criar a próxima atividade.
          const nextCodigo = nextCodigoFromCurrent(atividadeForm.codigo || '');
          await load();
          setAtividadeForm({ ...EMPTY_ATIV, codigo: nextCodigo });
        } else {
          // "Salvar" simples: fecha o modal, igual às outras entidades (Seção/Responsável/Investimento).
          setAtividadeModal(false);
          load();
        }
      } else {
        setAtividadeModal(false);
        load();
      }
    } else {
      toast.error(res.error || 'Erro ao salvar atividade');
    }
    setSaving(false);
  };

  const handleAtivDelete = async () => {
    if (!id || !atividadeDelete) return;
    const res = await projetotAtividadeService.delete(id, atividadeDelete.id);
    if (res.success) { toast.success('Atividade excluída'); setAtividadeDelete(null); load(); }
    else toast.error(res.error || 'Erro ao excluir atividade');
  };

  // ────── DRAG & DROP ─────────────────────────────────────────────────────────

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragId(event.active.id as string);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over) return;

    // closestCenter sempre resolve para o alvo mais próximo, mesmo sem sobreposição real —
    // exige overlap geométrico mínimo para não mover/reordenar ao soltar longe de qualquer linha/seção.
    const activeRect = active.rect.current.translated;
    if (!activeRect || !rectsOverlap(activeRect, over.rect)) return;

    const atividadeId = active.id as string;
    const overId = over.id as string;
    const atividade = todasAtividades.find(a => a.id === atividadeId);
    if (!atividade) return;

    const overIsSecao = (projeto?.secoes || []).some(s => s.id === overId);
    const overAtividade = overIsSecao ? undefined : todasAtividades.find(a => a.id === overId);
    const targetSecaoId = overIsSecao ? overId : overAtividade?.secao_id;
    if (!targetSecaoId) return;

    if (atividade.secao_id === targetSecaoId) {
      // Reordenação dentro da mesma seção
      if (!id || !overAtividade || overAtividade.id === atividade.id) return;
      const secao = (projeto?.secoes || []).find(s => s.id === targetSecaoId);
      if (!secao) return;
      const atuais = sortAtividades(secao.atividades || []);
      const oldIndex = atuais.findIndex(a => a.id === atividade.id);
      const newIndex = atuais.findIndex(a => a.id === overAtividade.id);
      if (oldIndex === -1 || newIndex === -1) return;
      const novaOrdem = arrayMove(atuais, oldIndex, newIndex).map(a => a.id);
      const res = await projetotAtividadeService.reordenar(id, targetSecaoId, novaOrdem);
      if (res.success) load();
      else toast.error(res.error || 'Erro ao reordenar atividades');
      return;
    }

    const targetSecao = (projeto?.secoes || []).find(s => s.id === targetSecaoId);
    if (!targetSecao) return;
    setPendingMove({ atividade, targetSecao });
  };

  const handleMoveConfirm = async () => {
    if (!id || !pendingMove) return;
    const res = await projetotAtividadeService.moveAtividade(id, pendingMove.atividade.id, pendingMove.targetSecao.id);
    if (res.success) {
      toast.success(`Atividade movida para "${pendingMove.targetSecao.titulo}"`);
      load();
    } else {
      toast.error(res.error || 'Erro ao mover atividade');
    }
    setPendingMove(null);
  };

  // ────── RESPONSÁVEIS ────────────────────────────────────────────────────────

  const openRespCreate = () => {
    setRespEdit(null);
    setRespForm(EMPTY_RESP);
    setRespModal(true);
  };

  const openRespEdit = (r: CronogramaResponsavel) => {
    setRespEdit(r);
    setRespForm({ nome: r.nome, cargo: r.cargo || '', email: r.email || '' });
    setRespModal(true);
  };

  const handleRespSave = async () => {
    if (!id || !respForm.nome) { toast.error('Nome é obrigatório'); return; }
    setSaving(true);
    const res = respEdit
      ? await projetotResponsavelService.update(id, respEdit.id, respForm)
      : await projetotResponsavelService.create(id, respForm);
    if (res.success) {
      toast.success(respEdit ? 'Responsável atualizado' : 'Responsável criado');
      setRespModal(false);
      // Se foi criado a partir do formulário de atividade, já seleciona o novo responsável (até o limite de 5)
      if (!respEdit && res.data?.id && atividadeModal) {
        setAtividadeForm(f => {
          const ids = f.responsaveis_ids || [];
          return ids.includes(res.data.id) || ids.length >= 5
            ? f
            : { ...f, responsaveis_ids: [...ids, res.data.id] };
        });
      }
      load();
    }
    else toast.error(res.error || 'Erro ao salvar responsável');
    setSaving(false);
  };

  // Marcar direto um usuário da empresa na caixa de responsáveis da atividade: cria o cadastro
  // de CronogramaResponsavel na hora (mesmo endpoint do "+ Novo responsável"), sem abrir modal.
  const handleSelecionarUsuarioComoResponsavel = async (usuario: Usuario) => {
    if (!id) return;
    setCriandoRespUsuarioId(usuario.id);
    const res = await projetotResponsavelService.create(id, { nome: usuario.nome, email: usuario.email });
    setCriandoRespUsuarioId(null);
    if (res.success && res.data) {
      const novoId = res.data.id;
      setCriadosNomeById(prev => ({ ...prev, [novoId]: usuario.nome }));
      setAtividadeForm(f => {
        const ids = f.responsaveis_ids || [];
        return ids.includes(novoId) || ids.length >= 5 ? f : { ...f, responsaveis_ids: [...ids, novoId] };
      });
      load();
    } else {
      toast.error(res.error || 'Erro ao adicionar responsável');
    }
  };

  const handleRespDelete = async () => {
    if (!id || !respDelete) return;
    const res = await projetotResponsavelService.delete(id, respDelete.id);
    if (res.success) { toast.success('Responsável excluído'); setRespDelete(null); load(); }
    else toast.error(res.error || 'Erro ao excluir responsável');
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <AppLayout>
        <div className="space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-64 w-full" />
        </div>
      </AppLayout>
    );
  }

  if (!projeto) {
    return (
      <AppLayout>
        <div className="text-center text-muted-foreground">Projeto não encontrado.</div>
      </AppLayout>
    );
  }

  const responsaveis = projeto.responsaveis || [];
  const emailsInternos = new Set(usuarios.filter(u => u.email).map(u => u.email!.toLowerCase()));
  const responsaveisInternos = responsaveis.filter(r => r.email && emailsInternos.has(r.email.toLowerCase()));
  const responsaveisExternos = responsaveis.filter(r => !(r.email && emailsInternos.has(r.email.toLowerCase())));

  // Caixa de responsáveis do modal de Atividade: já cadastrados no projeto (interno + externo)
  // + usuários da empresa que ainda não têm cadastro de responsável aqui (aparecem pra marcar
  // direto, sem precisar passar pelo "+ Novo responsável" antes).
  const emailsComResponsavel = new Set(responsaveis.filter(r => r.email).map(r => r.email!.toLowerCase()));
  const usuariosSemResponsavel = usuarios.filter(u => u.email && !emailsComResponsavel.has(u.email.toLowerCase()));
  type OpcaoResponsavel =
    | { kind: 'existente'; id: string; nome: string }
    | { kind: 'usuario'; usuario: Usuario };
  const opcoesResponsaveis: OpcaoResponsavel[] = [
    ...responsaveis.map(r => ({ kind: 'existente' as const, id: r.id, nome: r.nome })),
    ...usuariosSemResponsavel.map(u => ({ kind: 'usuario' as const, usuario: u })),
  ].sort((a, b) => {
    const nomeA = a.kind === 'existente' ? a.nome : a.usuario.nome;
    const nomeB = b.kind === 'existente' ? b.nome : b.usuario.nome;
    return nomeA.localeCompare(nomeB);
  });

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="rounded-lg border bg-card p-4 space-y-3">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3 min-w-0">
              <Button variant="ghost" size="icon" onClick={() => navigate('/projetos')}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <h1 className="text-2xl font-bold tracking-tight min-w-0">{projeto.titulo}</h1>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {podeEditar && (
                <Button variant="outline" size="sm" className="h-9 gap-2" onClick={() => setStatusFlowOpen(true)}>
                  <GitBranch className="h-4 w-4" />
                  Aprovação
                </Button>
              )}
              {podeEditar && (
                <Button variant="outline" size="sm" className="h-9 gap-2" onClick={() => setShareDialogOpen(true)}>
                  <Share2 className="h-4 w-4" />
                  Compartilhar
                </Button>
              )}
              <Button variant="outline" size="sm" className="h-9 gap-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50" onClick={handleExport} disabled={exporting}>
                <Download className="h-4 w-4" />
                {exporting ? 'Exportando...' : 'Excel'}
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-3 border-t flex-wrap">
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              Status
              <ProjetoStatusBadge status={projeto.status_projeto} />
            </div>
            <span className="h-1 w-1 rounded-full bg-border" />
            {projeto.tipo?.nome && <Badge variant="outline">{projeto.tipo.nome}</Badge>}
            {projeto.area && <Badge variant="outline">{projeto.area}</Badge>}
            <span className="h-1 w-1 rounded-full bg-border" />
            <span className="text-sm text-muted-foreground">Líder: <b className="font-medium text-foreground">{projeto.lider?.nome || '—'}</b></span>
            <span className="h-1 w-1 rounded-full bg-border" />
            {!projeto.data_inicio && !projeto.data_fim ? (
              <span className="text-sm text-muted-foreground italic">Datas não definidas</span>
            ) : (
              <span className="text-sm text-muted-foreground">
                {fmtDate(projeto.data_inicio)} → {fmtDate(projeto.data_fim)}
              </span>
            )}

            <div className="flex items-center gap-3 ml-auto min-w-[160px]">
              <span className="text-sm font-bold tabular-nums">{progresso}%</span>
              <Progress value={progresso} className={cn("w-32 h-2", getProgressIndicatorStyle(progresso))} />
            </div>
          </div>

          {(vinculos.organogramas.length > 0 || vinculos.fluxogramas.length > 0) && (
            <div className="flex items-center gap-1.5 pt-2 flex-wrap">
              <span className="text-xs text-muted-foreground">Vinculado em:</span>
              {vinculos.organogramas.map(v => (
                <Link
                  key={`org-${v.node_id}`}
                  to={`/organograma/${v.organograma_id}`}
                  className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground hover:border-foreground/40"
                  title={v.node_label || undefined}
                >
                  <GitBranch className="h-3 w-3" />
                  {v.organograma_nome || 'Organograma'}
                </Link>
              ))}
              {vinculos.fluxogramas.map(v => (
                <Link
                  key={`flx-${v.node_id}`}
                  to={`/fluxogramas/${v.fluxograma_id}`}
                  className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground hover:border-foreground/40"
                  title={v.node_label || undefined}
                >
                  <Share2 className="h-3 w-3" />
                  {v.fluxograma_nome || 'Fluxograma'}
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="atividades">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <TabsList>
              <TabsTrigger value="atividades" className="gap-1.5">
                <LayoutList className="h-4 w-4" />Atividades
              </TabsTrigger>
              <TabsTrigger value="dashboard" className="gap-1.5">
                <BarChart3 className="h-4 w-4" />Dashboard
              </TabsTrigger>
              <TabsTrigger value="responsaveis" className="gap-1.5">
                <Users className="h-4 w-4" />Responsáveis
                {responsaveis.length > 0 && (
                  <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">{responsaveis.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="investimentos" className="gap-1.5">
                <Wallet className="h-4 w-4" />Investimentos
                {investimentos.length > 0 && (
                  <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">{investimentos.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="historico" className="gap-1.5">
                <Clock className="h-4 w-4" />Histórico
                {historico.length > 0 && (
                  <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">{historico.length}</Badge>
                )}
              </TabsTrigger>
            </TabsList>
            {podeEditar && (
              <Button variant="secondary" size="sm" className="h-9 gap-1.5" onClick={openSecaoCreate}>
                <Plus className="h-3.5 w-3.5" />Nova Seção
              </Button>
            )}
          </div>

          {/* ── Tab: Atividades ───────────────────────────────────────────────── */}
          <TabsContent value="atividades" className="space-y-4 mt-4">
            {/* Barra de controles */}
            <div className="rounded-lg border bg-card p-3 flex flex-col sm:flex-row items-start sm:items-center gap-2">
              <Select value={filtroSecao || 'all'} onValueChange={v => setFiltroSecao(v === 'all' ? '' : v)}>
                <SelectTrigger className="sm:w-56 h-8 text-sm">
                  <SelectValue placeholder="Todas as seções" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as seções</SelectItem>
                  {(projeto.secoes || []).sort((a, b) => a.ordem - b.ordem).map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.numero} — {s.titulo}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filtroStatus || 'all'} onValueChange={v => setFiltroStatus(v === 'all' ? '' : v as CronogramaAtividadeStatus)}>
                <SelectTrigger className="sm:w-44 h-8 text-sm">
                  <SelectValue placeholder="Todos os status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os status</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([val, cfg]) => (
                    <SelectItem key={val} value={val}>{cfg.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex gap-1 ml-auto">
                <BotaoColunas gerenciador={colGerenciadorAtividades} className="h-8" />
                <Button variant="outline" size="sm" className="h-8 gap-1 text-xs" onClick={() => setExpandidas(new Set((projeto.secoes || []).map(s => s.id)))}>
                  <ChevronDown className="h-3.5 w-3.5" />Expandir tudo
                </Button>
                <Button variant="outline" size="sm" className="h-8 gap-1 text-xs" onClick={() => setExpandidas(new Set())}>
                  <ChevronUp className="h-3.5 w-3.5" />Recolher tudo
                </Button>
              </div>
            </div>

            {(projeto.secoes || []).length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  Nenhuma seção criada. Clique em <strong>Nova Seção</strong> para começar.
                </CardContent>
              </Card>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
              {(projeto.secoes || [])
                .sort((a, b) => a.ordem - b.ordem)
                .filter(s => !filtroSecao || s.id === filtroSecao)
                .map(secao => {
                  const expandida = expandidas.has(secao.id);
                  const toggleExpandir = () => setExpandidas(prev => {
                    const next = new Set(prev);
                    expandida ? next.delete(secao.id) : next.add(secao.id);
                    return next;
                  });
                  return (
                  <SecaoDropZone key={secao.id} id={secao.id}>
                  <Card className="overflow-hidden">
                    {/* Header da seção: texto leve, sem barra de fundo pesada */}
                    <div
                      className="flex items-center justify-between px-5 py-3 cursor-pointer select-none group"
                      onClick={toggleExpandir}
                    >
                      <div className="flex items-center gap-2">
                        {expandida
                          ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
                          : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                        }
                        <span className="text-[11px] font-bold tracking-wide text-muted-foreground">
                          {secao.numero} — {secao.titulo.toUpperCase()}
                        </span>
                        {secao.okr_gerado_id && (
                          <button
                            type="button"
                            onClick={e => { e.stopPropagation(); navigate(`/okrs?okr=${secao.okr_gerado_id}`); }}
                            title={`Esta seção virou um OKR: ${secaoConvertidas(secao)} de ${(secao.atividades || []).length} atividades já são Key Results dele — clique para abrir`}
                            className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-600 hover:bg-emerald-500/20 dark:text-emerald-400"
                          >
                            <Target className="h-3 w-3" />
                            OKR
                            <span className="tabular-nums">{secaoConvertidas(secao)}/{(secao.atividades || []).length}</span>
                            <ExternalLink className="h-2.5 w-2.5" />
                          </button>
                        )}
                        {!expandida && (
                          <span className="text-[11px] text-muted-foreground">
                            ({(secao.atividades || []).filter(a => !filtroStatus || a.status === filtroStatus).length} {(secao.atividades || []).filter(a => !filtroStatus || a.status === filtroStatus).length === 1 ? 'atividade' : 'atividades'}, recolhida)
                          </span>
                        )}
                      </div>
                      {podeEditar && (
                        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs text-muted-foreground" onClick={() => openAtivCreate(secao.id)}>
                            <Plus className="h-3 w-3" />Atividade
                          </Button>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" onClick={() => openSecaoEdit(secao)}>
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={() => setSecaoDelete(secao)}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Lista de atividades em linha (grade de colunas Cód./Atividade/Responsáveis/
                        Início/Término/Status/Prioridade/Prazo/Vínculo/Ações — ver ATUALIZACOES.md,
                        redesign da tabela de atividades). */}
                    {expandida && (() => {
                      const atvsVisiveis = sortAtividades(secao.atividades || [])
                        .filter(a => !filtroStatus || a.status === filtroStatus);
                      if (atvsVisiveis.length === 0) {
                        return (
                          <div className="text-center py-6 border-t text-xs text-muted-foreground">
                            {filtroStatus ? 'Nenhuma atividade com este status nesta seção.' : 'Nenhuma atividade nesta seção.'}
                          </div>
                        );
                      }
                      const colunasAtiv = colGerenciadorAtividades.visiveisOrdenadas as { id: ColunaAtividadeId; width: number; label: string }[];
                      const larguraMinima = 112 + 72 + 40 + (colunasAtiv.length + 1) * 12
                        + colunasAtiv.reduce((soma, c) => soma + c.width, 0);
                      return (
                        <DragScrollX>
                          <div style={{ minWidth: larguraMinima }}>
                            <AtividadesHeader
                              colunas={colunasAtiv}
                              onResize={(colId, deltaX) => colGerenciadorAtividades.redimensionar(colId, deltaX)}
                            />
                            <SortableContext items={atvsVisiveis.map(a => a.id)} strategy={verticalListSortingStrategy}>
                              {atvsVisiveis.map(atv => (
                                <AtividadeRow
                                  key={atv.id}
                                  atv={atv}
                                  podeEditar={podeEditar}
                                  onStatusChange={handleStatusChange}
                                  onOkrClick={abrirOkrDaAtividade}
                                  podeConverter={ehAdmin}
                                  onConvert={openConvert}
                                  onEdit={openAtivEdit}
                                  onDelete={setAtividadeDelete}
                                  cronogramaId={id || ''}
                                  subatividadesExpandida={subatividadesExpandidas.has(atv.id)}
                                  onToggleSubatividades={() => toggleSubatividades(atv.id)}
                                  onSubatividadesChange={() => load()}
                                  colunas={colunasAtiv}
                                />
                              ))}
                            </SortableContext>
                          </div>
                        </DragScrollX>
                      );
                    })()}
                  </Card>
                  </SecaoDropZone>
                  );
                })}
                <DragOverlay>
                  {activeDragId ? (() => {
                    const atv = todasAtividades.find(a => a.id === activeDragId);
                    return atv ? (
                      <div className="bg-popover border shadow-lg rounded px-3 py-2 text-sm font-medium text-foreground flex items-center gap-2 opacity-95">
                        <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="font-mono text-xs text-muted-foreground mr-1">{atv.codigo}</span>
                        {atv.titulo}
                      </div>
                    ) : null;
                  })() : null}
                </DragOverlay>
              </DndContext>
            )}
          </TabsContent>

          {/* ── Tab: Responsáveis ─────────────────────────────────────────────── */}
          <TabsContent value="responsaveis" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <CardTitle className="text-base">Responsáveis do Projeto</CardTitle>
                {podeEditar && (
                  <Button size="sm" className="gap-1.5" onClick={openRespCreate}>
                    <Plus className="h-3.5 w-3.5" />Responsável
                  </Button>
                )}
              </CardHeader>
              <CardContent className="p-0 space-y-4 pb-4">
                {responsaveis.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">Nenhum responsável cadastrado.</p>
                ) : (
                  <>
                    <div>
                      <p className="px-4 pt-3 pb-1 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Da empresa ({responsaveisInternos.length})
                      </p>
                      {responsaveisInternos.length === 0 ? (
                        <p className="px-4 pb-2 text-sm text-muted-foreground">Nenhum responsável com conta no sistema.</p>
                      ) : (
                        <ResponsavelTable list={responsaveisInternos} podeEditar={podeEditar} onEdit={openRespEdit} onDelete={setRespDelete} />
                      )}
                    </div>
                    <div>
                      <p className="px-4 pt-1 pb-1 text-xs font-medium text-muted-foreground uppercase tracking-wide border-t pt-3">
                        Externos ({responsaveisExternos.length})
                      </p>
                      {responsaveisExternos.length === 0 ? (
                        <p className="px-4 pb-2 text-sm text-muted-foreground">Nenhum responsável externo.</p>
                      ) : (
                        <ResponsavelTable list={responsaveisExternos} podeEditar={podeEditar} onEdit={openRespEdit} onDelete={setRespDelete} />
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Tab: Investimentos ────────────────────────────────────────────── */}
          <TabsContent value="investimentos" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <CardTitle className="text-base">Investimentos do Projeto</CardTitle>
                {podeEditar && (
                  <Button size="sm" className="gap-1.5" onClick={openInvestCreate}>
                    <Plus className="h-3.5 w-3.5" />Investimento
                  </Button>
                )}
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right w-32">Valor unitário</TableHead>
                      <TableHead className="text-center w-24">Qtd.</TableHead>
                      <TableHead className="text-right w-36">Subtotal</TableHead>
                      <TableHead className="w-20 text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {investimentos.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                          Nenhum investimento cadastrado.
                        </TableCell>
                      </TableRow>
                    ) : (
                      <>
                        {investimentos.map(inv => (
                          <TableRow key={inv.id}>
                            <TableCell className="font-medium">{inv.item}</TableCell>
                            <TableCell className="text-right">{brl(Number(inv.valor))}</TableCell>
                            <TableCell className="text-center">{inv.quantidade}</TableCell>
                            <TableCell className="text-right">{brl(Number(inv.valor) * inv.quantidade)}</TableCell>
                            <TableCell className="text-right">
                              {podeEditar && (
                                <div className="flex justify-end gap-1">
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openInvestEdit(inv)}>
                                    <Pencil className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={() => setInvestDelete(inv)}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-muted/40">
                          <TableCell colSpan={3} className="font-semibold text-right">Total</TableCell>
                          <TableCell className="text-right font-bold">{brl(totalInvestimentos)}</TableCell>
                          <TableCell />
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Tab: Dashboard ────────────────────────────────────────────────── */}
          <TabsContent value="dashboard" className="mt-4 space-y-4">
            {/* Cards por status */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {dashboard?.porStatus.map(s => (
                <Card key={s.status}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">{s.label}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">{s.count}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{s.percentual}% do total</p>
                    <Progress value={s.percentual} className={cn("h-1.5 mt-2", getProgressIndicatorStyle(s.percentual))} />
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Tabela responsáveis x status */}
            {(dashboard?.porResponsavel || []).length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Atividades por Responsável</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Responsável</TableHead>
                        <TableHead className="text-center">Não iniciado</TableHead>
                        <TableHead className="text-center">Em processo</TableHead>
                        <TableHead className="text-center">Finalizado</TableHead>
                        <TableHead className="text-center">Suspenso</TableHead>
                        <TableHead className="text-center">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(dashboard?.porResponsavel || []).map(r => {
                        const tot = r.nao_iniciado + r.em_processo + r.finalizado + r.suspenso;
                        return (
                          <TableRow key={r.nome}>
                            <TableCell className="font-medium">{r.nome}</TableCell>
                            <TableCell className="text-center">{r.nao_iniciado || 0}</TableCell>
                            <TableCell className="text-center">{r.em_processo || 0}</TableCell>
                            <TableCell className="text-center text-green-600 font-medium">{r.finalizado || 0}</TableCell>
                            <TableCell className="text-center text-red-500">{r.suspenso || 0}</TableCell>
                            <TableCell className="text-center font-bold">{tot}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ── Tab: Histórico ────────────────────────────────────────────────── */}
          <TabsContent value="historico" className="mt-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground">
                {historico.length === 0 ? 'Nenhuma ação registrada ainda.' : `${historico.length} ${historico.length === 1 ? 'registro' : 'registros'} encontrados`}
              </p>
              <Button variant="outline" size="sm" className="gap-1.5 print:hidden" onClick={() => window.print()}>
                <Printer className="h-4 w-4" />Exportar PDF
              </Button>
            </div>

            {historico.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  <Clock className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p>Nenhuma ação registrada ainda.</p>
                  <p className="text-xs mt-1">As ações realizadas neste projeto aparecerão aqui.</p>
                </CardContent>
              </Card>
            ) : (
              <div id="historico-print" className="relative">
                {/* Linha vertical da timeline */}
                <div className="absolute left-[22px] top-0 bottom-0 w-px bg-border" />

                <div className="space-y-1">
                  {historico.map((item, idx) => {
                    const acaoConfig = {
                      criou:         { icon: PlusCircle,     color: 'text-emerald-600', bg: 'bg-emerald-50',  label: 'Criou' },
                      atualizou:     { icon: Edit,           color: 'text-blue-600',    bg: 'bg-blue-50',     label: 'Atualizou' },
                      excluiu:       { icon: Trash,          color: 'text-rose-600',    bg: 'bg-rose-50',     label: 'Excluiu' },
                      alterou_status:{ icon: ArrowRightLeft, color: 'text-amber-600',   bg: 'bg-amber-50',    label: 'Status' },
                    }[item.acao];
                    const Icon = acaoConfig.icon;
                    const dt = new Date(item.createdAt);
                    const data = dt.toLocaleDateString('pt-BR');
                    const hora = dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

                    return (
                      <div key={item.id} className="flex gap-4 pl-2 py-2">
                        {/* Ícone */}
                        <div className={`relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 border-background ${acaoConfig.bg}`}>
                          <Icon className={`h-4 w-4 ${acaoConfig.color}`} />
                        </div>

                        {/* Conteúdo */}
                        <div className="flex-1 min-w-0 pt-1">
                          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                            <span className="text-sm font-medium text-foreground">{item.descricao}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-muted-foreground">{item.usuario_nome}</span>
                            <span className="text-muted-foreground/40 text-xs">·</span>
                            <span className="text-xs text-muted-foreground">{data} às {hora}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* ── Modal Seção ──────────────────────────────────────────────────────── */}
      <Dialog open={secaoModal} onOpenChange={setSecaoModal}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>{secaoEdit ? 'Editar Seção' : 'Nova Seção'}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-3 gap-3">
              <div className="min-w-0 space-y-1.5">
                <Label>Número *</Label>
                <Input type="number" value={secaoForm.numero} onChange={e => setSecaoForm(f => ({ ...f, numero: +e.target.value }))} className="w-full min-w-0" />
              </div>
              <div className="min-w-0 space-y-1.5 col-span-2">
                <Label>Ordem</Label>
                <Input type="number" value={secaoForm.ordem} onChange={e => setSecaoForm(f => ({ ...f, ordem: +e.target.value }))} className="w-full min-w-0" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Título *</Label>
              <Input value={secaoForm.titulo} onChange={e => setSecaoForm(f => ({ ...f, titulo: e.target.value }))} placeholder="Ex: Diagnóstico / Start Projeto" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="min-w-0 space-y-1.5">
                <Label>Data de Início</Label>
                <Input type="date" value={secaoForm.data_inicio || ''} onChange={e => setSecaoForm(f => ({ ...f, data_inicio: e.target.value }))} className="w-full min-w-0" />
              </div>
              <div className="min-w-0 space-y-1.5">
                <Label>Data de Término</Label>
                <Input type="date" value={secaoForm.data_termino || ''} onChange={e => setSecaoForm(f => ({ ...f, data_termino: e.target.value }))} className="w-full min-w-0" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSecaoModal(false)}>Cancelar</Button>
            <Button onClick={handleSecaoSave} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Modal Atividade ─────────────────────────────────────────────────── */}
      <Dialog open={atividadeModal} onOpenChange={setAtividadeModal}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader><DialogTitle>{atividadeEdit ? 'Editar Atividade' : 'Nova Atividade'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2 items-start">
            <div className="space-y-1.5">
              <div className="h-5 flex items-center">
                <Label>Código *</Label>
              </div>
              <Input placeholder="Ex: 1.1" value={atividadeForm.codigo || ''} onChange={e => setAtividadeForm(f => ({ ...f, codigo: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <div className="h-5 flex items-center justify-between">
                <Label>
                  Responsáveis{' '}
                  <span className="text-xs text-muted-foreground font-normal">(máx. 5)</span>
                </Label>
                <button
                  type="button"
                  className="text-xs text-primary underline underline-offset-2 hover:text-primary/80"
                  onClick={openRespCreate}
                >
                  + Novo responsável
                </button>
              </div>
              {(() => {
                const idsSelecionados = atividadeForm.responsaveis_ids || [];
                const limite = idsSelecionados.length >= 5;
                const nomePorId = new Map<string, string>([
                  ...responsaveis.map(r => [r.id, r.nome] as const),
                  ...Object.entries(criadosNomeById),
                ]);
                const buscaNormalizada = respSelectSearch.trim().toLowerCase();
                const opcoesFiltradas = opcoesResponsaveis.filter(op => {
                  const nome = op.kind === 'existente' ? op.nome : op.usuario.nome;
                  return nome.toLowerCase().includes(buscaNormalizada);
                });
                return (
                  <>
                    <Popover open={respSelectOpen} onOpenChange={setRespSelectOpen}>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring"
                        >
                          <span className={cn('truncate text-left', idsSelecionados.length === 0 && 'text-muted-foreground')}>
                            {idsSelecionados.length === 0
                              ? 'Selecione os responsáveis'
                              : `${idsSelecionados.length} selecionado${idsSelecionados.length > 1 ? 's' : ''}`}
                          </span>
                          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                        <Command shouldFilter={false}>
                          <CommandInput placeholder="Buscar responsável..." value={respSelectSearch} onValueChange={setRespSelectSearch} />
                          <CommandList>
                            <CommandEmpty>Nenhum responsável encontrado.</CommandEmpty>
                            <CommandGroup>
                              {opcoesFiltradas.map(op => {
                                if (op.kind === 'existente') {
                                  const selecionado = idsSelecionados.includes(op.id);
                                  const desabilitado = !selecionado && limite;
                                  const toggle = () => {
                                    if (desabilitado) return;
                                    setAtividadeForm(f => {
                                      const ids = f.responsaveis_ids || [];
                                      return {
                                        ...f,
                                        responsaveis_ids: selecionado
                                          ? ids.filter(i => i !== op.id)
                                          : [...ids, op.id].slice(0, 5),
                                      };
                                    });
                                  };
                                  return (
                                    <CommandItem
                                      key={`e-${op.id}`}
                                      value={op.nome}
                                      disabled={desabilitado}
                                      onSelect={toggle}
                                    >
                                      <Checkbox
                                        checked={selecionado}
                                        disabled={desabilitado}
                                        onClick={e => e.stopPropagation()}
                                        onCheckedChange={toggle}
                                        className="mr-2"
                                      />
                                      {op.nome}
                                    </CommandItem>
                                  );
                                }
                                // Usuário da empresa ainda sem cadastro de responsável neste projeto —
                                // selecionar cria o cadastro (CronogramaResponsavel) na hora.
                                const criando = criandoRespUsuarioId === op.usuario.id;
                                const selecionarUsuario = () => {
                                  if (limite || criando) return;
                                  handleSelecionarUsuarioComoResponsavel(op.usuario);
                                };
                                return (
                                  <CommandItem
                                    key={`u-${op.usuario.id}`}
                                    value={op.usuario.nome}
                                    disabled={limite || criando}
                                    onSelect={selecionarUsuario}
                                  >
                                    <Checkbox
                                      checked={false}
                                      disabled={limite || criando}
                                      onClick={e => e.stopPropagation()}
                                      onCheckedChange={selecionarUsuario}
                                      className="mr-2"
                                    />
                                    {op.usuario.nome}{criando ? ' …' : ''}
                                  </CommandItem>
                                );
                              })}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    {idsSelecionados.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {idsSelecionados.map(rid => (
                          <Badge key={rid} variant="secondary" className="gap-1 font-normal">
                            {nomePorId.get(rid) || '...'}
                            <button
                              type="button"
                              onClick={() => setAtividadeForm(f => ({ ...f, responsaveis_ids: (f.responsaveis_ids || []).filter(i => i !== rid) }))}
                              className="ml-1 rounded-full outline-none hover:text-destructive"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
            <div className="space-y-1.5">
              <div className="h-5 flex items-center">
                <Label>Título *</Label>
              </div>
              <Input placeholder="Descrição resumida da atividade" value={atividadeForm.titulo || ''} onChange={e => setAtividadeForm(f => ({ ...f, titulo: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <div className="h-5 flex items-center">
                <Label>Status</Label>
              </div>
              <Select value={atividadeForm.status || 'nao_iniciado'} onValueChange={val => setAtividadeForm(f => ({ ...f, status: val as CronogramaAtividadeStatus }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_CONFIG).map(([val, cfg]) => (
                    <SelectItem key={val} value={val}>{cfg.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <div className="h-5 flex items-center">
                <Label>Prioridade</Label>
              </div>
              <Select value={atividadeForm.prioridade || 'media'} onValueChange={val => setAtividadeForm(f => ({ ...f, prioridade: val as CronogramaAtividadePrioridade }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PRIORIDADE_CONFIG).map(([val, cfg]) => (
                    <SelectItem key={val} value={val}>{cfg.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <div className="h-5 flex items-center gap-1.5">
                <Label>Dependência</Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button" className="text-muted-foreground hover:text-foreground">
                      <HelpCircle className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-64">
                    Use pra travar a ordem de execução: escolha outra atividade deste projeto da
                    qual esta depende. Enquanto a atividade escolhida não for finalizada, esta
                    fica bloqueada em "Não iniciado".
                  </TooltipContent>
                </Tooltip>
              </div>
              <Select
                value={atividadeForm.depende_de_id || '__nenhuma__'}
                onValueChange={val => setAtividadeForm(f => ({ ...f, depende_de_id: val === '__nenhuma__' ? null : val }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__nenhuma__">Nenhuma</SelectItem>
                  {todasAtividades
                    .filter(a => a.id !== atividadeEdit?.id)
                    .map(a => (
                      <SelectItem key={a.id} value={a.id}>{a.codigo} — {a.titulo}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label>Detalhamento</Label>
              <Textarea rows={3} placeholder="Descrição detalhada..." value={atividadeForm.detalhamento || ''} onChange={e => setAtividadeForm(f => ({ ...f, detalhamento: e.target.value }))} />
            </div>
            <div className="min-w-0 space-y-1.5">
              <Label>Data de Início</Label>
              <Input type="date" value={atividadeForm.data_inicio || ''} onChange={e => setAtividadeForm(f => ({ ...f, data_inicio: e.target.value }))} className="w-full min-w-0" />
            </div>
            <div className="min-w-0 space-y-1.5">
              <Label>Data de Término</Label>
              <Input type="date" value={atividadeForm.data_termino || ''} onChange={e => setAtividadeForm(f => ({ ...f, data_termino: e.target.value }))} className="w-full min-w-0" />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label>Acompanhamento</Label>
              <Textarea rows={2} placeholder="Observações de acompanhamento..." value={atividadeForm.acompanhamento || ''} onChange={e => setAtividadeForm(f => ({ ...f, acompanhamento: e.target.value }))} />
            </div>
            <div className="col-span-2 flex items-center gap-2 pt-1">
              <Checkbox
                id="anexo_obrigatorio"
                checked={!!atividadeForm.anexo_obrigatorio}
                onCheckedChange={val => setAtividadeForm(f => ({ ...f, anexo_obrigatorio: Boolean(val) }))}
              />
              <Label htmlFor="anexo_obrigatorio" className="cursor-pointer font-normal">
                Exigir anexo para finalizar esta atividade
              </Label>
            </div>
            {atividadeEdit && id && (
              <div className="col-span-2 pt-2 border-t">
                <AnexosSection
                  cronogramaId={id}
                  atividadeId={atividadeEdit.id}
                  anexos={anexos}
                  onAnexosChange={setAnexos}
                />
              </div>
            )}
            {!atividadeEdit && (
              <div className="col-span-2 pt-2 border-t">
                <PendingAnexosSection files={pendingFiles} onFilesChange={setPendingFiles} />
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAtividadeModal(false)}>Cancelar</Button>
            {!atividadeEdit && (
              <Button variant="secondary" onClick={() => handleAtivSave(true)} disabled={saving}>
                {saving ? 'Salvando...' : 'Salvar e adicionar outro'}
              </Button>
            )}
            <Button onClick={() => handleAtivSave(false)} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Modal Responsável ────────────────────────────────────────────────── */}
      <Dialog open={respModal} onOpenChange={setRespModal}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>{respEdit ? 'Editar Responsável' : 'Novo Responsável'}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            {/* Funcionários da empresa já são selecionáveis direto no dropdown de Responsáveis
                da atividade — este cadastro manual é sempre para alguém externo (sem conta no
                sistema). */}
            <div className="space-y-1.5">
              <Label>Nome *</Label>
              <Input value={respForm.nome} onChange={e => setRespForm(f => ({ ...f, nome: e.target.value }))} placeholder="Ex: João Silva" />
            </div>
            <div className="space-y-1.5">
              <Label>Cargo</Label>
              <Input value={respForm.cargo} onChange={e => setRespForm(f => ({ ...f, cargo: e.target.value }))} placeholder="Ex: Consultor" />
            </div>
            <div className="space-y-1.5">
              <Label>E-mail</Label>
              <Input
                type="email"
                value={respForm.email}
                onChange={e => setRespForm(f => ({ ...f, email: e.target.value }))}
                placeholder="joao@empresa.com"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRespModal(false)}>Cancelar</Button>
            <Button onClick={handleRespSave} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Modal Investimento ───────────────────────────────────────────────── */}
      <Dialog open={investModal} onOpenChange={setInvestModal}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>{investEdit ? 'Editar Investimento' : 'Novo Investimento'}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label>Item *</Label>
              <Input value={investForm.item} onChange={e => setInvestForm(f => ({ ...f, item: e.target.value }))} placeholder="Ex: Licenças de software" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="min-w-0 space-y-1.5">
                <Label>Valor unitário (R$)</Label>
                <Input type="number" min={0} step="0.01" className="w-full min-w-0" value={investForm.valor} onChange={e => setInvestForm(f => ({ ...f, valor: Number(e.target.value) }))} />
              </div>
              <div className="min-w-0 space-y-1.5">
                <Label>Quantidade</Label>
                <Input type="number" min={1} className="w-full min-w-0" value={investForm.quantidade} onChange={e => setInvestForm(f => ({ ...f, quantidade: Number(e.target.value) }))} />
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              Subtotal: <strong>{brl(investForm.valor * investForm.quantidade)}</strong>
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvestModal(false)}>Cancelar</Button>
            <Button onClick={handleInvestSave} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Fluxo de aprovação ───────────────────────────────────────────────── */}
      <ProjetoStatusFlow
        projeto={{ titulo: projeto.titulo, status: projeto.status_projeto }}
        open={statusFlowOpen}
        onOpenChange={setStatusFlowOpen}
        onStatusChange={handleStatusProjetoChange}
        somenteEnviarParaAprovacao={!ehAdmin}
      />

      {/* ── Confirmações de exclusão ─────────────────────────────────────────── */}
      <AlertDialog open={!!investDelete} onOpenChange={open => !open && setInvestDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir investimento?</AlertDialogTitle>
            <AlertDialogDescription>
              O item <strong>{investDelete?.item}</strong> será removido permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleInvestDelete} className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!secaoDelete} onOpenChange={open => !open && setSecaoDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir seção?</AlertDialogTitle>
            <AlertDialogDescription>Todas as atividades desta seção também serão excluídas.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleSecaoDelete} className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!atividadeDelete} onOpenChange={open => !open && setAtividadeDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir atividade?</AlertDialogTitle>
            <AlertDialogDescription>A atividade <strong>{atividadeDelete?.titulo}</strong> será excluída permanentemente.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleAtivDelete} className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!respDelete} onOpenChange={open => !open && setRespDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir responsável?</AlertDialogTitle>
            <AlertDialogDescription>O responsável <strong>{respDelete?.nome}</strong> será desvinculado das atividades.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleRespDelete} className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!pendingMove} onOpenChange={open => !open && setPendingMove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mover atividade?</AlertDialogTitle>
            <AlertDialogDescription>
              Deseja mover a atividade <strong>{pendingMove?.atividade.titulo}</strong> para a seção <strong>{pendingMove?.targetSecao.titulo}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingMove(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleMoveConfirm}>Mover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Compartilhar projeto</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {shareToken ? (
              <>
                <p className="text-sm text-muted-foreground">
                  Qualquer pessoa com este link pode visualizar o projeto em tempo real, sem precisar de login.
                </p>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={`${window.location.origin}/c/${shareToken}`}
                    className="font-mono text-xs"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/c/${shareToken}`);
                      toast.success('Link copiado!');
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <Button
                  variant="destructive"
                  className="w-full gap-2"
                  onClick={handleRevogarLink}
                  disabled={shareLoading}
                >
                  <Unlink className="h-4 w-4" />
                  {shareLoading ? 'Revogando...' : 'Revogar link'}
                </Button>
              </>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  Gere um link público para compartilhar este projeto. Qualquer pessoa com o link poderá visualizá-lo sem precisar de login.
                </p>
                <Button className="w-full gap-2" onClick={handleCompartilhar} disabled={shareLoading}>
                  <Share2 className="h-4 w-4" />
                  {shareLoading ? 'Gerando...' : 'Gerar link público'}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Modal: Converter atividade em OKR / Key Result ──────────────────── */}
      <Dialog open={!!convertAtiv} onOpenChange={open => !open && setConvertAtiv(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Converter em OKR / Key Result</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div className="space-y-2 rounded-md bg-muted px-3 py-2 text-sm">
              <p>
                <span className="text-muted-foreground">OKR: </span>
                {convertLoading ? (
                  <span className="text-muted-foreground">carregando…</span>
                ) : convertOkrSecao && !['cancelado', 'feito'].includes(convertOkrSecao.status) ? (
                  <>
                    <span className="font-medium text-foreground">{convertOkrSecao.titulo}</span>
                    <span className="text-muted-foreground"> (já existe)</span>
                  </>
                ) : (
                  <>
                    <span className="font-medium text-foreground">{convertSecao?.titulo}</span>
                    <span className="text-muted-foreground">
                      {convertOkrSecao
                        ? ` (será criado — o OKR "${convertOkrSecao.titulo}" da seção está ${convertOkrSecao.status === 'cancelado' ? 'cancelado' : 'concluído'})`
                        : ' (será criado com o nome da seção)'}
                    </span>
                  </>
                )}
              </p>
              <p>
                <span className="text-muted-foreground">Key Result: </span>
                <span className="font-mono text-xs text-muted-foreground mr-1">{convertAtiv?.codigo}</span>
                <span className="font-medium text-foreground">{convertAtiv?.titulo}</span>
              </p>
            </div>
            <p className="text-xs text-muted-foreground">
              O OKR novo fica no departamento do líder do projeto. O progresso do Key Result acompanha o status da
              atividade: finalizada = 100%, não iniciada = 0%.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertAtiv(null)} disabled={converting}>Cancelar</Button>
            <Button onClick={handleConverter} disabled={converting || convertLoading}>
              {converting ? 'Convertendo...' : 'Converter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
